import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faXmark, faArrowUpFromBracket, faSquarePlus } from '@fortawesome/free-solid-svg-icons';

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Si ya está instalada en modo standalone, no mostrar nada
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Si el usuario ya cerró el banner antes, no mostrar
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (dismissed) return;

    // Detectar iOS (iPhone, iPad, iPod)
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    if (ios) {
      // iOS no soporta beforeinstallprompt — mostrar instrucciones manuales
      setShowBanner(true);
    } else {
      // Chrome/Android: esperar el evento beforeinstallprompt
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowBanner(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShowBanner(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', '1');
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-w-lg mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/icons/icon-72x72.png" alt="Vecino Seguro" className="w-8 h-8 rounded-lg" />
            <span className="text-white font-bold text-sm">Vecino Seguro</span>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white/80 hover:text-white transition-colors"
          >
            <FontAwesomeIcon icon={faXmark} className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          {isIOS ? (
            <>
              <p className="text-gray-800 font-semibold text-sm mb-2">
                Instala la app en tu iPhone
              </p>
              <ol className="text-gray-600 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="bg-teal-100 text-teal-700 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                  <span>
                    Toca el botón <FontAwesomeIcon icon={faArrowUpFromBracket} className="text-blue-500 mx-1" />
                    <strong>Compartir</strong> en Safari
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-teal-100 text-teal-700 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                  <span>
                    Selecciona <FontAwesomeIcon icon={faSquarePlus} className="text-blue-500 mx-1" />
                    <strong>"Agregar a pantalla de inicio"</strong>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-teal-100 text-teal-700 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                  <span>Toca <strong>"Agregar"</strong> para confirmar</span>
                </li>
              </ol>
              <button
                onClick={handleDismiss}
                className="mt-3 w-full text-center text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                No mostrar de nuevo
              </button>
            </>
          ) : (
            <>
              <p className="text-gray-800 font-semibold text-sm mb-1">
                ¡Instala Vecino Seguro!
              </p>
              <p className="text-gray-500 text-xs mb-3">
                Accede más rápido desde tu pantalla de inicio, sin abrir el navegador.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleInstall}
                  className="flex-1 flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold py-2.5 px-4 rounded-xl transition-colors"
                >
                  <FontAwesomeIcon icon={faDownload} className="w-4 h-4" />
                  Instalar
                </button>
                <button
                  onClick={handleDismiss}
                  className="px-4 py-2.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  Ahora no
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
